import { router } from "@inertiajs/react";
import ChannelMenu, { type ScopeNode } from "@/components/ChannelMenu";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/ToggleGroup";
import PeriodMenu, { type PeriodPreset, type PeriodValue } from "../../Revenue/components/PeriodMenu";
import type { RowKind } from "./ActualsTable";
import Inline from "./progress/Inline";

export type PeriodKey = "today" | "this_month" | "plan_period";

const PRESETS: PeriodPreset<PeriodKey>[] = [
  { key: "today", label: "Today" },
  { key: "this_month", label: "This month" },
  { key: "plan_period", label: "Plan period" },
];

interface Props {
  scopeTree: ScopeNode[];
  scope: { id: string; name: string };
  hrefForScope: (slug: string) => string;
  categoryTree: ScopeNode[];
  category: { id: string; name: string } | null;
  hrefForCategory: (slug: string) => string;
  period: PeriodValue<PeriodKey>;
  planBegin: string;
  planEnd: string;
  hrefForPeriod: (period: PeriodValue<PeriodKey>) => string;
  rowKind: RowKind;
  hrefForRowKind: (kind: RowKind) => string;
}

// The table's Scope, Category, Period and Break down by, inline in its
// section bar: they change the table only.
export default function ActualsToolbar(props: Props) {
  const { period, rowKind } = props;
  const go = (href: string) => router.get(href, {}, { preserveScroll: true });
  // Keep the page mounted on a period change, so the open menu stays open while
  // custom dates are picked (the same as the Revenue page).
  const goPeriod = (href: string) => router.get(href, {}, { preserveScroll: true, preserveState: true });

  return (
    <div className="flex flex-wrap items-center gap-5">
      <Inline label="Scope">
        <ChannelMenu
          scopeTree={props.scopeTree}
          currentNodeId={props.scope.id}
          currentNodeName={props.scope.name}
          hrefForChannel={props.hrefForScope}
          requireLeaf={false}
        />
      </Inline>
      <Inline label="Category">
        <ChannelMenu
          scopeTree={props.categoryTree}
          currentNodeId={props.category?.id ?? "all"}
          currentNodeName={props.category?.name ?? "All categories"}
          hrefForChannel={props.hrefForCategory}
          requireLeaf={false}
          label="Category"
        />
      </Inline>
      <Inline label="Period">
        <PeriodMenu
          value={period}
          presets={PRESETS}
          minDate={props.planBegin}
          maxDate={props.planEnd}
          onChange={(patch) => goPeriod(props.hrefForPeriod({ ...period, ...patch }))}
        />
      </Inline>
      <Inline label="Break down by">
        <ToggleGroup type="single" value={rowKind} onValueChange={(v) => v && go(props.hrefForRowKind(v as RowKind))}>
          <ToggleGroupItem value="products" size="sm">Products</ToggleGroupItem>
          <ToggleGroupItem value="categories" size="sm">Category</ToggleGroupItem>
          <ToggleGroupItem value="channels" size="sm">Channel</ToggleGroupItem>
        </ToggleGroup>
      </Inline>
    </div>
  );
}
